'use client';

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react';
import { apiClient } from '@/lib/apiClient';

export type AppRole = 'admin' | 'editor' | 'investigador';

export interface SessionUser {
	email: string;
	role: AppRole | string;
	sub?: number;
	idInvestigador?: number | null;
	esAdmin?: boolean;
	// Slug del perfil público. `null` cuando la cuenta no tiene investigador
	// vinculado (p. ej. un admin de gestión). Permite enlazar directo al perfil
	// sin pasar por el redirect de `/mi-perfil`. Se refresca solo: un cambio de
	// nombre regenera el slug y recarga la página, remontando este provider.
	slug?: string | null;
	fotoPerfilUrl?: string | null;
	// Nombre según el formato elegido (o nombre1+apellido1 si no hay perfil/formato).
	nombre?: string | null;
	// Estamento de la persona (académico/directivo/funcionario/…), no el rol de sistema.
	estamento?: string | null;
	// Tutoriales spotlight (R34) ya completados por el usuario — para no auto-mostrarlos.
	tutorialesCompletados?: string[];
}

type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthSessionContextValue {
	user: SessionUser | null;
	status: SessionStatus;
	refreshSession: () => Promise<void>;
}

const AuthSessionContext = createContext<AuthSessionContextValue | undefined>(
	undefined,
);

async function getCurrentUser(): Promise<SessionUser | null> {
	try {
		const response = await apiClient('/auth/me');
		if (!response.ok) return null;
		return (await response.json()) as SessionUser;
	} catch {
		return null;
	}
}

export function AuthSessionProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<SessionUser | null>(null);
	const [status, setStatus] = useState<SessionStatus>('loading');

	const refreshSession = useCallback(async () => {
		const nextUser = await getCurrentUser();
		setUser(nextUser);
		setStatus(nextUser ? 'authenticated' : 'unauthenticated');
	}, []);

	useEffect(() => {
		let isMounted = true;

		getCurrentUser().then((nextUser) => {
			if (!isMounted) return;
			setUser(nextUser);
			setStatus(nextUser ? 'authenticated' : 'unauthenticated');
		});

		return () => {
			isMounted = false;
		};
	}, []);

	const value = useMemo<AuthSessionContextValue>(
		() => ({
			user,
			status,
			refreshSession,
		}),
		[user, status, refreshSession],
	);

	return (
		<AuthSessionContext.Provider value={value}>
			{children}
		</AuthSessionContext.Provider>
	);
}

export function useAuthSession() {
	const context = useContext(AuthSessionContext);
	if (!context) {
		throw new Error(
			'useAuthSession debe usarse dentro de <AuthSessionProvider>.',
		);
	}
	return context;
}
